import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, FileDown, Pencil } from "lucide-react";
import { exportToCsv } from "@/lib/exportCsv";
import { DatePicker } from "@/components/DatePicker";
import { toast } from "sonner";

const TYPES = ["Periyodik Bakım", "Onarım", "Saha Arızası"];
const EMPTY = { machine_code: "", operation_type: "Periyodik Bakım", work_done: "", changed_parts: "", location: "", date: new Date().toISOString().slice(0, 10), hours_km: "", service_info: "", cost: 0, reading_at_service: 0 };

const TYPE_COLORS = {
  "Periyodik Bakım": "bg-blue-100 text-blue-800 border-blue-200",
  "Onarım": "bg-amber-100 text-amber-800 border-amber-200",
  "Saha Arızası": "bg-red-100 text-red-800 border-red-200",
};

export default function Maintenance() {
  const { user } = useAuth();
  const canEdit = user?.role !== "viewer";
  const [rows, setRows] = useState([]);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const locations = [...new Set(machines.map((m) => m.location).filter(Boolean))].sort();

  const pickMachine = (code) => {
    const m = machines.find((x) => x.code === code);
    setForm((f) => ({ ...f, machine_code: code, location: (f.location || (m ? m.location : "")) || "" }));
  };

  const load = async () => {
    setLoading(true);
    try {
      const [m, mc] = await Promise.all([api.get("/maintenance"), api.get("/machines")]);
      setRows(m.data); setMachines(mc.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.machine_code || !form.work_done) {
      toast.error("Makine kodu ve yapılan işlem zorunludur");
      return;
    }
    try {
      const payload = { ...form, cost: parseFloat(form.cost) || 0, reading_at_service: parseFloat(form.reading_at_service) || 0 };
      if (editId) {
        await api.put(`/maintenance/${editId}`, payload);
        toast.success("Bakım kaydı güncellendi");
      } else {
        await api.post("/maintenance", payload);
        toast.success("Bakım kaydı eklendi");
      }
      setOpen(false); load();
    } catch (e) { toast.error("İşlem başarısız"); }
  };

  const openEdit = (r) => {
    setForm({
      machine_code: r.machine_code, operation_type: r.operation_type || "Periyodik Bakım",
      work_done: r.work_done || "", changed_parts: r.changed_parts || "", location: r.location || "",
      date: r.date || new Date().toISOString().slice(0, 10), hours_km: r.hours_km || "",
      service_info: r.service_info || "", cost: r.cost || 0, reading_at_service: r.reading_at_service || 0,
    });
    setEditId(r.id); setOpen(true);
  };

  const remove = async (id) => { await api.delete(`/maintenance/${id}`); toast.success("Silindi"); load(); };

  const exportCsv = () => {
    if (rows.length === 0) { toast.info("Aktarılacak veri yok"); return; }
    exportToCsv("bakim_onarim", [
      { key: "machine_code", label: "Makine Kodu" },
      { key: "operation_type", label: "İşlem Tipi" },
      { key: "work_done", label: "Yapılan İşlem" },
      { key: "changed_parts", label: "Değişen Parçalar" },
      { key: "location", label: "Şantiye / Depo" },
      { key: "date", label: "İşlem Tarihi" },
      { key: "hours_km", label: "Saat / KM" },
      { key: "service_info", label: "Servis / Tamirci" },
      { key: "cost", label: "Maliyet (TL)" },
    ], rows);
  };

  return (
    <Layout
      title="Bakım / Onarım Takibi"
      subtitle="Periyodik bakım, onarım ve saha arıza kayıtları"
      actions={
        <div className="flex items-center gap-2">
          <Button data-testid="export-maintenance-btn" onClick={exportCsv} variant="outline" className="rounded-sm gap-2">
            <FileDown className="w-4 h-4" /> Excel'e Aktar
          </Button>
          {canEdit && (
          <Button data-testid="add-maintenance-btn" onClick={() => { setForm(EMPTY); setEditId(null); setOpen(true); }} className="rounded-sm gap-2 bg-primary hover:bg-primary/90">
            <Plus className="w-4 h-4" /> Kayıt Ekle
          </Button>
          )}
        </div>
      }
    >
      <Card className="rounded-sm border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-zinc-50">
              <TableHead className="text-xs font-bold uppercase tracking-wide">Makine</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wide">İşlem Tipi</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wide">Yapılan İşlem</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wide">Değişen Parçalar</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wide">Şantiye / Depo</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wide">Tarih</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wide">Saat / KM</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wide">Servis</TableHead>
              <TableHead className="text-right text-xs font-bold uppercase tracking-wide">Maliyet</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={10} className="text-center py-10 text-muted-foreground">Yükleniyor...</TableCell></TableRow>}
            {!loading && rows.length === 0 && <TableRow><TableCell colSpan={10} className="text-center py-10 text-muted-foreground">Kayıt bulunamadı</TableCell></TableRow>}
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`maintenance-row-${r.id}`} className="odd:bg-white even:bg-zinc-50/50">
                <TableCell className="font-mono font-semibold">{r.machine_code}</TableCell>
                <TableCell><Badge variant="outline" className={`rounded-sm text-xs font-bold ${TYPE_COLORS[r.operation_type]}`}>{r.operation_type}</Badge></TableCell>
                <TableCell className="max-w-[220px]">{r.work_done}</TableCell>
                <TableCell className="text-muted-foreground text-sm">{r.changed_parts || "-"}</TableCell>
                <TableCell className="text-sm font-medium">{r.location || "-"}</TableCell>
                <TableCell className="font-mono text-sm">{r.date ? r.date.split("-").reverse().join(".") : "-"}</TableCell>
                <TableCell className="font-mono text-sm">{r.hours_km || "-"}</TableCell>
                <TableCell className="text-sm">{r.service_info || "-"}</TableCell>
                <TableCell className="text-right font-mono font-semibold">{(r.cost || 0).toLocaleString("tr-TR")} ₺</TableCell>
                <TableCell className="text-right">
                  {canEdit && (
                  <>
                  <Button data-testid={`edit-maintenance-${r.id}`} variant="ghost" size="icon" className="h-8 w-8 rounded-sm" onClick={() => openEdit(r)} title="Düzenle"><Pencil className="w-4 h-4" /></Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button data-testid={`delete-maintenance-${r.id}`} variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-red-600"><Trash2 className="w-4 h-4" /></Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="rounded-sm">
                      <AlertDialogHeader><AlertDialogTitle>Kayıt silinsin mi?</AlertDialogTitle><AlertDialogDescription>Bu bakım kaydı silinecek.</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter><AlertDialogCancel className="rounded-sm">Vazgeç</AlertDialogCancel><AlertDialogAction className="rounded-sm bg-red-600 hover:bg-red-700" onClick={() => remove(r.id)}>Sil</AlertDialogAction></AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader><DialogTitle className="font-display">{editId ? "Bakım / Onarım Düzenle" : "Yeni Bakım / Onarım Kaydı"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <Field label="Makine/Araç Kodu *">
              <Select value={form.machine_code} onValueChange={pickMachine}>
                <SelectTrigger data-testid="mnt-machine" className="rounded-sm"><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                <SelectContent>{machines.map((m) => <SelectItem key={m.id} value={m.code}>{m.code} — {m.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="İşlem Tipi">
              <Select value={form.operation_type} onValueChange={(v) => setForm({ ...form, operation_type: v })}>
                <SelectTrigger data-testid="mnt-type" className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <div className="col-span-2"><Field label="Yapılan İşlem *"><Textarea data-testid="mnt-work" value={form.work_done} onChange={(e) => setForm({ ...form, work_done: e.target.value })} className="rounded-sm" rows={2} /></Field></div>
            <div className="col-span-2"><Field label="Değişen Parçalar"><Input data-testid="mnt-parts" value={form.changed_parts} onChange={(e) => setForm({ ...form, changed_parts: e.target.value })} className="rounded-sm" /></Field></div>
            <div className="col-span-2">
              <Field label="Şantiye / Depo (maliyet bu sahaya işlenir)">
                <Input data-testid="mnt-location" list="mnt-locations" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Örn. Kocaeli Şantiyesi / Merkez Atölye" className="rounded-sm" />
                <datalist id="mnt-locations">{locations.map((l) => <option key={l} value={l} />)}</datalist>
              </Field>
              <p className="text-xs text-muted-foreground mt-1">Boş bırakırsanız makinenin mevcut lokasyonu kullanılır. Şantiye bazlı maliyet özet panosunda toplanır.</p>
            </div>
            <Field label="İşlem Tarihi"><DatePicker testid="mnt-date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} /></Field>
            <Field label="Çalışma Saati / KM"><Input data-testid="mnt-hours" value={form.hours_km} onChange={(e) => setForm({ ...form, hours_km: e.target.value })} className="rounded-sm" /></Field>
            <Field label="Servis / Tamirci"><Input data-testid="mnt-service" value={form.service_info} onChange={(e) => setForm({ ...form, service_info: e.target.value })} className="rounded-sm" /></Field>
            <Field label="Maliyet (₺)"><Input data-testid="mnt-cost" type="number" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} className="rounded-sm" /></Field>
            <div className="col-span-2">
              <Field label="Bakımdaki Sayaç (saat/km) — opsiyonel">
                <Input data-testid="mnt-reading" type="number" value={form.reading_at_service} onChange={(e) => setForm({ ...form, reading_at_service: e.target.value })} placeholder="Bu bakım hangi saat/km'de yapıldı?" className="rounded-sm" />
              </Field>
              <p className="text-xs text-muted-foreground mt-1">Kaydettiğinizde makinenin bakım hatırlatıcısı otomatik sıfırlanır (sonraki bakım buradan itibaren hesaplanır).</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>İptal</Button>
            <Button data-testid="save-maintenance-btn" className="rounded-sm bg-primary hover:bg-primary/90" onClick={save}>Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function Field({ label, children }) {
  return (<div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">{label}</Label>{children}</div>);
}
